import { useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography, CircularProgress, TextField } from "@mui/material";
import {
  HelpOutline as HelpIcon,
  Close as CloseIcon,
  Send as SendIcon,
  AutoAwesome as AiIcon,
} from "@mui/icons-material";
import UniversalActionButton, {
  UNIVERSAL_BUTTON_STYLES,
} from "../common/UniversalActionButton";
import { useWebLLM } from "../../contexts/WebLLMContext";
import { buildHelpSystemPrompt } from "../../data/appHelpGuide";

const SYSTEM_PROMPT = buildHelpSystemPrompt();
const SUGGESTIONS = [
  "How do I add a contact?",
  "How do I add a note or photo to a contact?",
  "How do I check off and delete a ToDo item?",
  "How do I share a public file link?",
];

const HelpLauncher = () => {
  const { engine, ready, loading, downloading, progress, error, isMobile, init } = useWebLLM();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [generating, setGenerating] = useState(false);
  const abortRef = useRef(false);
  const listRef = useRef(null);

  // Start loading the local model the first time Help is opened.
  useEffect(() => {
    if (open && !ready && !loading && !error) init();
  }, [open, ready, loading, error, init]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const statusLine = (() => {
    if (error) return "The local AI model failed to load (needs Chrome/Edge with WebGPU).";
    if (downloading) return `Downloading the AI model (one time)... ${Math.round((progress || 0) * 100)}%`;
    if (loading) return "Loading the AI model...";
    if (!ready) return "Preparing the AI model...";
    return null;
  })();

  const send = useCallback(async (text) => {
    const q = (text ?? input).trim();
    if (!q || !engine || !ready || generating) return;
    const next = [...messages, { role: "user", content: q }];
    setMessages(next);
    setInput("");
    setGenerating(true);
    abortRef.current = false;
    try {
      const chunks = await engine.chat.completions.create({
        messages: [{ role: "system", content: SYSTEM_PROMPT }, ...next.map((m) => ({ role: m.role, content: m.content }))],
        temperature: 0.2,
        top_p: 0.85,
        max_tokens: 400,
        stream: true,
      });
      let acc = "";
      setMessages((prev) => [...prev, { role: "assistant", content: "", streaming: true }]);
      for await (const chunk of chunks) {
        if (abortRef.current) break;
        const delta = chunk.choices?.[0]?.delta?.content || "";
        if (delta) {
          acc += delta;
          const current = acc;
          setMessages((prev) => {
            const copy = [...prev];
            copy[copy.length - 1] = { role: "assistant", content: current, streaming: true };
            return copy;
          });
        }
      }
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "assistant", content: acc || "Sorry, I could not generate a response.", streaming: false };
        return copy;
      });
    } catch (err) {
      console.error("Help chat failed:", err);
      setMessages((prev) => [...prev, { role: "assistant", content: "Something went wrong generating a response.", streaming: false }]);
    } finally {
      setGenerating(false);
    }
  }, [input, engine, ready, generating, messages]);

  // Hidden entirely on mobile (the local model is desktop-only).
  if (isMobile) return null;

  return (
    <>
      <UniversalActionButton
        styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
        label="Help"
        icon={<HelpIcon fontSize="small" />}
        circleColor={open ? "#3949AB" : "#3F51B5"}
        iconColor="#FFFFFF"
        labelColor="text.secondary"
        onClick={() => setOpen((v) => !v)}
        title="Help"
        aria-label="Help"
      />

      {open && (
        <Box
          sx={{
            position: "fixed", bottom: 20, right: 20, zIndex: 1300,
            width: 360, maxWidth: "calc(100vw - 40px)", height: 520, maxHeight: "calc(100vh - 40px)",
            display: "flex", flexDirection: "column",
            bgcolor: "#fff", borderRadius: 2, overflow: "hidden",
            boxShadow: "0 12px 32px rgba(26,35,126,0.28)", border: "1px solid #E8EAF6",
          }}
        >
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1.25, bgcolor: "#3F51B5", color: "#fff" }}>
            <AiIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 700, flex: 1 }}>Help</Typography>
            {!ready && !error && <CircularProgress size={14} sx={{ color: "#fff" }} />}
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: "#fff" }} aria-label="Close help">
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box ref={listRef} sx={{ flex: 1, overflowY: "auto", p: 1.5, bgcolor: "#F4F5FB" }}>
            {messages.length === 0 ? (
              <Box>
                <Typography sx={{ fontSize: 13, color: "#1A1F36", mb: 1 }}>
                  Ask me how to use ContextStory. For example:
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {SUGGESTIONS.map((s) => (
                    <Box
                      key={s}
                      onClick={() => ready && send(s)}
                      sx={{
                        px: 1.25, py: 0.75, borderRadius: 1.5, fontSize: 12.5,
                        bgcolor: "#fff", border: "1px solid #E8EAF6", cursor: ready ? "pointer" : "default",
                        color: "#283593", "&:hover": ready ? { borderColor: "#3F51B5", bgcolor: "#E8EAF6" } : {},
                        opacity: ready ? 1 : 0.6,
                      }}
                    >
                      {s}
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {messages.map((m, i) => (
                  <Box
                    key={i}
                    sx={{
                      alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                      maxWidth: "85%", px: 1.25, py: 0.75, borderRadius: 1.5, fontSize: 13, whiteSpace: "pre-wrap",
                      bgcolor: m.role === "user" ? "#3F51B5" : "#fff",
                      color: m.role === "user" ? "#fff" : "#1A1F36",
                      border: m.role === "user" ? "none" : "1px solid #E8EAF6",
                    }}
                  >
                    {m.content || (m.streaming ? "…" : "")}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Status + input */}
          {statusLine && (
            <Typography sx={{ px: 1.5, py: 0.5, fontSize: 11, color: error ? "#C62828" : "#5C6BC0", bgcolor: "#F4F5FB" }}>
              {statusLine}
            </Typography>
          )}
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5, p: 1, borderTop: "1px solid #E8EAF6" }}>
            <TextField
              placeholder={ready ? "Ask a question..." : "Loading model..."}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              disabled={!ready || generating}
              fullWidth multiline maxRows={4} size="small"
            />
            <IconButton color="primary" onClick={() => send()} disabled={!ready || generating || !input.trim()} aria-label="Send">
              <SendIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}
    </>
  );
};

export default HelpLauncher;
